<template>
    <app-layout title="Reports">
        <page-header>
            <template #title>Reports - All</template>

            <template #actions>
                <jet-tertiary-button type="button" class="py-1" @click="handlePrint" :disabled="form.processing || form.report == 'transactions' || !canRunReport || !report_data.length" >
                    <img src="/images/icons/print.svg" width="16" height="16" alt="Print">
                </jet-tertiary-button>

                <jet-tertiary-button
                    type="button"
                    class="py-1"
                    @click="handleExport"
                    :disabled="form.processing || !form.report || !canRunReport || !reportHasExportableRows"
                    title="Export to Excel"
                >
                    <img src="/images/icons/excel.svg" width="16" height="16" alt="Export to Excel">
                </jet-tertiary-button>

                <jet-button type="button" @click="handleSubmit" :disabled="form.processing || !canRunReport" >
                    Run Report
                </jet-button>
            </template>
        </page-header>

        <reports-navigation />

        <div class="px-8">
            <div class="flex">
                <form
                    class="flex flex-1 gap-x-2 gap-y-2"
                    :class="itemSalesFilterLayout ? 'flex-col w-full' : 'flex-wrap'"
                    @submit.prevent="handleSubmit"
                >
                    <div class="flex gap-x-2" :class="itemSalesFilterLayout ? 'w-full max-w-2xl' : 'w-full xl:w-1/2'">
                        <div class="flex flex-col flex-1">
                            <jet-label for="report" value="Report" class="whitespace-nowrap"/>
                            <multiselect class="multi-sm whitespace-nowrap h-7 text-base"
                                v-model="form.report"
                                :options="reportOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                :canClear="false"
                                :canDeselect="false"
                                placeholder="Select a Report"
                                :classes="{singleLabel: 'w-full px-2 whitespace-nowrap'}"
                            />
                        </div>

                        <div class="flex flex-col flex-1">
                            <jet-label for="date_range" value="Date Range" />
                            <datepicker inputClassName="dp-custom-input"
                                @update:modelValue="handleUpdateDateRange"
                                v-model="date"
                                :is24="false"
                                :enableTimePicker="false"
                                :autoApply="true"
                                range
                                twoCalendars
                                placeholder="Select Date Range"
                                :format="formatDateRange"
                                weekStart=0
                            />
                        </div>

                    </div>

                    <!-- Filters -->
                    <div class="flex flex-col" :class="itemSalesFilterLayout ? 'w-full' : 'flex-1'">
                        <jet-label for="filters" value="Filters" />
                        <div v-if="form.report == null" class="text-gray-400">Select a report to view filters</div>
                        <div v-else :class="itemSalesFilterLayout ? 'flex flex-col flex-1 gap-y-2' : 'flex flex-1 gap-x-2'">
                            <div :class="itemSalesFilterLayout ? 'flex flex-nowrap gap-x-2' : 'contents'">
                            <div v-show="showOrderTypeFilter" class="w-56">
                                <multiselect class="multi-custom h-7 text-base"
                                    v-model="form.order_type"
                                    :options="orderTypeOptionsForReport"
                                    valueProp="value"
                                    trackBy="value"
                                    label="label"
                                    :classes="{singleLabel: 'w-full px-2'}"
                                    :placeholder="['shorts', 'order_defects'].includes(form.report) ? 'All' : 'Retail & Wholesale'"
                                />
                            </div>

                            <div v-show="form.report === 'transactions'" class="w-56">
                                <multiselect class="multi-custom h-7 text-base"
                                    v-model="form.transaction_type"
                                    :options="transactionTypeOptions"
                                    valueProp="value"
                                    trackBy="value"
                                    label="label"
                                    :classes="{singleLabel: 'w-full px-2'}"
                                    placeholder="Charges & Refunds"
                                />
                            </div>
                            
                            <div v-show="showItemTypeFilter" class="w-56">
                                <multiselect class="multi-custom h-7 text-base"
                                    v-model="form.item_type"
                                    :options="itemTypeOptions"
                                    valueProp="value"
                                    trackBy="value"
                                    label="label"
                                    :classes="{singleLabel: 'w-full px-2'}"
                                    placeholder="All Item Types"
                                />
                            </div>
                            
                            <div v-show="showCreditCardBrandFilter" class="w-40">
                                <multiselect class="multi-custom h-7 text-base"
                                    v-model="form.credit_card_brand"
                                    :options="creditCardBrandOptions"
                                    valueProp="value"
                                    trackBy="value"
                                    label="label"
                                    :classes="{singleLabel: 'w-full px-2'}"
                                    placeholder="All Brands"
                                />
                            </div>

                            <div v-show="showFilterByFilter" class="w-40">
                                <multiselect class="multi-sm h-7 text-base"
                                    v-model="form.filter_by"
                                    :options="filterOptions"
                                    valueProp="value"
                                    trackBy="label"
                                    label="label"
                                    :canClear="false"
                                    :canDeselect="false"
                                    :classes="{singleLabel: 'w-full px-2'}"
                                />
                            </div>

                            <div v-show="showCategoryFilter" class="w-48">
                                <multiselect class="multi-custom h-7 text-base"
                                    v-model="form.category_ids"
                                    :options="categoryOptions"
                                    mode="multiple"
                                    valueProp="id"
                                    trackBy="slug"
                                    label="title"
                                    :searchable="true"
                                    placeholder="Category"
                                >
                                    <template v-slot:option="{ option }">
                                        <span :class="{ 'ml-5' : option.parent_id != null }">{{ option.title }} </span>
                                    </template>
                                </multiselect>
                            </div>

                            <div v-show="showCustomerTypeFilter && !itemSalesFilterLayout" class="w-60">
                                <multiselect class="multi-custom h-7 text-base"
                                             v-model="form.customer_types"
                                             :options="customerTypeOptions"
                                             mode="multiple"
                                             valueProp="id"
                                             trackBy="slug"
                                             label="title"
                                             :searchable="true"
                                             placeholder="All Customer Types"
                                >
                                    <template v-slot:option="{ option }">
                                        <span :class="{ 'ml-5' : option.parent_id != null }">{{ option.title }} </span>
                                    </template>
                                </multiselect>
                            </div>

                            <div v-show="showPricingLevelFilter && !itemSalesFilterLayout" class="w-56">
                                <multiselect class="multi-custom h-7 text-base"
                                             v-model="form.pricing_levels"
                                             :options="pricing_levels"
                                             mode="multiple"
                                             valueProp="value"
                                             trackBy="value"
                                             label="label"
                                             :searchable="true"
                                             placeholder="All Pricing Levels"
                                >
                                </multiselect>
                            </div>

                            <div v-show="showShippingMethodFilter && !itemSalesFilterLayout" class="w-60">
                                <multiselect class="multi-custom h-7 text-base"
                                    v-model="form.shipping_method"
                                    :options="shippingMethodOptions"
                                    valueProp="value"
                                    trackBy="value"
                                    label="label"
                                    :classes="{singleLabel: 'w-full px-2'}"
                                    placeholder="All Shipping Methods"
                                />
                            </div>
                            
                            <div v-show="form.report === 'order_defects'" class="w-60">
                                <multiselect class="multi-custom h-7 text-base"
                                    v-model="form.picker_id"
                                    :options="pickers"
                                    valueProp="id"
                                    trackBy="name"
                                    label="name"
                                    placeholder="All Pickers"
                                    searchable
                                />
                            </div>

                            <div v-show="form.report === 'order_defects'" class="w-60">
                                <multiselect class="multi-custom h-7 text-base"
                                    v-model="form.checker_id"
                                    :options="checkers"
                                    valueProp="id"
                                    trackBy="name"
                                    label="name"
                                    placeholder="All Checkers"
                                    searchable
                                />
                            </div>

                            <div v-show="form.report === 'order_defects'" class="w-60">
                                <multiselect class="multi-custom h-7 text-base"
                                    v-model="form.return_reason"
                                    :options="returnReasons"
                                    placeholder="All Defects"
                                    searchable
                                    mode="tags"
                                />
                            </div>

                            <div v-show="form.report === 'item_sales' && !itemSalesFilterLayout" class="w-60 relative">
                                <multiselect
                                    v-model="form.purchaser_id"
                                    :options="purchasers"
                                    valueProp="id"
                                    trackBy="id"
                                    label="name"
                                    placeholder="All Purchasers"
                                    mode="multiple"
                                    :closeOnSelect="false"
                                     class="multi-custom h-7 text-base"
                                    :classes="{multipleLabel: 'w-full px-2'}"
                                >
                                    <template v-slot:multiplelabel="{ values }">
                                        <div class="multiselect-multiple-label">
                                            {{ values.length }} purchaser{{ values.length > 1 ? 's' : ''}}
                                        </div>
                                    </template>
                                </multiselect>
                                <div v-show="form.purchaser_id && form.purchaser_id.length" class="absolute -bottom-6 w-full truncate">
                                    <span v-for="(single_purchaser_id, index) in form.purchaser_id" :key="single_purchaser_id" class="text-xs">
                                        {{ purchasers.find(p => p.id == single_purchaser_id).name }}{{index == form.purchaser_id.length - 1 ? '' : ', '}}
                                    </span>
                                </div>
                            </div>
                            </div>

                            <div v-if="itemSalesFilterLayout" class="flex flex-nowrap gap-x-2">
                                <div v-show="showCustomerTypeFilter" class="w-60">
                                    <multiselect class="multi-custom h-7 text-base"
                                                 v-model="form.customer_types"
                                                 :options="customerTypeOptions"
                                                 mode="multiple"
                                                 valueProp="id"
                                                 trackBy="slug"
                                                 label="title"
                                                 :searchable="true"
                                                 placeholder="All Customer Types"
                                    >
                                        <template v-slot:option="{ option }">
                                            <span :class="{ 'ml-5' : option.parent_id != null }">{{ option.title }} </span>
                                        </template>
                                    </multiselect>
                                </div>

                                <div v-show="showPricingLevelFilter" class="w-56">
                                    <multiselect class="multi-custom h-7 text-base"
                                                 v-model="form.pricing_levels"
                                                 :options="pricing_levels"
                                                 mode="multiple"
                                                 valueProp="value"
                                                 trackBy="value"
                                                 label="label"
                                                 :searchable="true"
                                                 placeholder="All Pricing Levels"
                                    >
                                    </multiselect>
                                </div>

                                <div v-show="showShippingMethodFilter" class="w-60">
                                    <multiselect class="multi-custom h-7 text-base"
                                        v-model="form.shipping_method"
                                        :options="shippingMethodOptions"
                                        valueProp="value"
                                        trackBy="value"
                                        label="label"
                                        :classes="{singleLabel: 'w-full px-2'}"
                                        placeholder="All Shipping Methods"
                                    />
                                </div>

                                <div class="w-60 relative">
                                    <multiselect
                                        v-model="form.purchaser_id"
                                        :options="purchasers"
                                        valueProp="id"
                                        trackBy="id"
                                        label="name"
                                        placeholder="All Purchasers"
                                        mode="multiple"
                                        :closeOnSelect="false"
                                         class="multi-custom h-7 text-base"
                                        :classes="{multipleLabel: 'w-full px-2'}"
                                    >
                                        <template v-slot:multiplelabel="{ values }">
                                            <div class="multiselect-multiple-label">
                                                {{ values.length }} purchaser{{ values.length > 1 ? 's' : ''}}
                                            </div>
                                        </template>
                                    </multiselect>
                                    <div v-show="form.purchaser_id && form.purchaser_id.length" class="absolute -bottom-6 w-full truncate">
                                        <span v-for="(single_purchaser_id, index) in form.purchaser_id" :key="single_purchaser_id" class="text-xs">
                                            {{ purchasers.find(p => p.id == single_purchaser_id).name }}{{index == form.purchaser_id.length - 1 ? '' : ', '}}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="xl:hidden self-end">
                        <jet-tertiary-button type="button" class="py-1 ml-4 h-7" @click="handleReset" >
                            Reset
                        </jet-tertiary-button>
                    </div>

                </form>

                <div class="hidden xl:block self-end">
                    <jet-tertiary-button type="button" class="py-1 ml-4 h-7" @click="handleReset" >
                        Reset
                    </jet-tertiary-button>
                </div>
            </div>
        </div>

        <div class="px-8 py-6">

            <div v-if="!form.processing" >
                <div v-if="form.report != null && report_data.length > 0 || Object.keys(report_data).length">

                    <div v-if="!form.isDirty" class="rounded overflow-y-auto border report-container" style="max-height:70vh">
                        <!-- Report Vue Components -->
                        <sales v-if="form.report === 'sales' && !form.isDirty" @sort="handleSort" :data="report_data" :sort_column="sort_column" :sort_direction="sort_direction"/>
                        <item-sales v-if="form.report === 'item_sales' && !form.isDirty" @sort="handleSort" :data="report_data" :sort_column="sort_column" :sort_direction="sort_direction"/>
                        <shorts v-if="form.report === 'shorts' && !form.isDirty" @sort="handleSort" :data="report_data" :sort_column="sort_column" :sort_direction="sort_direction" :filters="filters"/>
                        <transactions v-if="form.report === 'transactions' && !form.isDirty" @sort="handleSort" :data="report_data" :sort_column="sort_column" :sort_direction="sort_direction"/>
                        <order-defects v-if="form.report === 'order_defects' && !form.isDirty" @sort="handleSort" :data="report_data" :sort_column="sort_column" :sort_direction="sort_direction"/>
                        <forecasted-shorts v-if="form.report === 'forecasted_shorts' && !form.isDirty" @sort="handleSort" :data="report_data" :sort_column="sort_column" :sort_direction="sort_direction"/>
                    </div>

                    <div v-else class="text-center text-gray-400 text-2xl pt-10">
                        Click Run Report to refresh data
                    </div>

                </div>

                <div v-show="showNoReportDataMsg" class="text-center text-gray-400 text-2xl pt-10">
                    No data for selected report parameters
                </div>

                <div v-show="showInitialReportMsg" class="text-center text-gray-400 text-2xl pt-10">
                    Select report parameters & click Run Report button
                </div>
            </div>

            <div v-else class="text-center text-gray-400 text-2xl pt-10">
                Retrieving report data...
            </div>
        </div>

    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import { usePage } from '@inertiajs/inertia-vue3'
import UseUserAuth from '@/Composables/UseUserAuth';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import ReportsNavigation from '@/Pages/Reports/ReportsNavigation';
import JetButton from '@/Jetstream/Button';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetLabel from '@/Jetstream/Label';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import Multiselect from '@vueform/multiselect';
import Datepicker from '@vuepic/vue-datepicker';
import { DateTime } from "luxon";

// Reports
import Sales from '@/Pages/Reports/All/Sales';
import ItemSales from '@/Pages/Reports/All/ItemSales';
import Shorts from '@/Pages/Reports/All/Shorts';
import Transactions from '@/Pages/Reports/All/Transactions';
import OrderDefects from '@/Pages/Reports/All/OrderDefects';
import ForecastedShorts from '@/Pages/Reports/All/ForecastedShorts';

import '@vueform/multiselect/themes/default.css';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
    setup(props) {

        const form = useForm({
            report: props.filters.report,
            credit_card_brand: props.filters.credit_card_brand ?? null,
            order_type: props.filters.order_type === 'schools' ? 'school' : (props.filters.order_type ?? null),
            item_type: props.filters.item_type ?? null,
            filter_by: props.filters.filter_by ?? 'ship_date',
            category_ids: props.filters.category_ids ?? [],
            customer_types: props.filters.customer_types,
            pricing_levels: props.filters.pricing_levels,
            shipping_method: props.filters.shipping_method ?? null,
            from: props.filters.from,
            to: props.filters.to,
            checker_id: props.filters.checker_id ?? null,
            picker_id: props.filters.picker_id ?? null,
            transaction_type: props.filters.transaction_type ?? null,
            return_reason: props.filters.return_reason ?? [],
            purchaser_id: props.filters.purchaser_id ?? [],
            sort_column: props.sort_column,
            sort_direction: props.sort_direction ?? 'asc',
            default_sort_column: 'id',
        });

        const { hasFinanceRole } = UseUserAuth(usePage().props.value.user);

        return {
            form,
            hasFinanceRole,
        }
    },

    components: {
        AppLayout,
        PageHeader,
        ReportsNavigation,
        JetButton,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
        JetTertiaryButton,
        Multiselect,
        Datepicker,
        Sales,
        ItemSales,
        Shorts,
        Transactions,
        OrderDefects,
        ForecastedShorts,
    },

    props: ['data', 'filters', 'sort_column', 'sort_direction', 'categories', 'customer_types', 'pricing_levels', 'sales_reps', 'purchasers', 'pickers', 'checkers', 'returnReasons'],
    data() {
        return {
            report_data: this.data,

            date: null,

            creditCardBrandOptions: [
                { value:'amex', label:'Amex' },
                { value:'discover', label:'Discover' },
                { value:'mastercard', label:'Mastercard' },
                { value:'visa', label:'Visa' },
                { value:'other', label:'Other' },
            ],

            filterOptions: [
                { value:'order_date', label:'Order Date' },
                { value:'ship_date', label:'Ship Date' },
            ],

            reportOptions: [
                { value: 'sales', label: 'Sales' },
                { value: 'item_sales', label: 'Item Sales' },
                { value: 'shorts', label: 'Shorts' },
                { value: 'transactions', label: 'Transactions' },
                { value: 'order_defects', label: 'Order Defects' },
                { value: 'forecasted_shorts', label: 'Forecasted Shorts' }
            ],

            orderTypeOptions: [
                { value: 'retail', label: 'Retail' },
                { value: 'wholesale', label: 'Wholesale' },
                { value: 'school', label: 'Schools' },
            ],

            itemTypeOptions: [
                { value: 'processed', label: 'Processed' },
                { value: 'non_processed', label: 'Non-Processed' },
            ],
            
            shippingMethodOptions: [
                { value: 'delivery', label: 'Delivery' },
                { value: 'pickup', label: 'Pickup' },
            ],

            transactionTypeOptions: [
                { value: 'charges', label: 'Charges' },
                { value: 'refunds', label: 'Refunds' },
            ],
        }
    },

    methods: {
        handleSubmit: _.debounce(function () {
            this.form.submit('post', route('reports.all.report'), { preserveState: true });
        }, 250),

        handleSort (sort) {
            this.form.sort_column = sort.column;
            this.form.sort_direction = sort.direction;
            this.handleSubmit();
        },

        handleReset() {
            this.report_data = [];
            this.date = null;

            this.form.report = null;
            this.form.order_type = null;
            this.form.item_type = null;
            this.form.filter_by = 'ship_date';
            this.form.category_ids = [];
            this.form.customer_types = null;
            this.form.pricing_levels = null;
            this.form.shipping_method = null;
            this.form.from = null;
            this.form.to = null;
            this.form.purchaser_id = [];
            this.form.sort_column = null;
            this.form.sort_direction = 'asc';
            this.form.checker_id = null;
            this.form.picker_id = null;
            this.form.transaction_type = null;
            this.form.return_reason = [];

            this.handleSubmit();
        },

        handlePrint() {
            window.open((route('reports.all.print', {
                report: this.form.report,
                order_type: this.form.order_type,
                item_type: this.form.item_type,
                filter_by: this.form.filter_by,
                category_ids: this.form.category_ids,
                customer_types: this.form.customer_types,
                pricing_levels: this.form.pricing_levels,
                shipping_method: this.form.shipping_method,
                from: this.form.from,
                to: this.form.to,
                sort_column: this.form.sort_column,
                sort_direction: this.form.sort_direction,
                picker_id: this.form.picker_id,
                checker_id: this.form.checker_id,
                return_reason: this.form.return_reason,
                purchaser_id: this.form.purchaser_id,
                transaction_type: this.form.transaction_type
            } )), "_blank");
        },

        handleExport() {
            window.location = route('reports.all.export', {
                report: this.form.report,
                from: this.form.from,
                to: this.form.to,
                filter_by: this.form.filter_by,
                order_type: this.form.order_type,
                item_type: this.form.item_type,
                category_ids: this.form.category_ids,
                customer_types: this.form.customer_types,
                pricing_levels: this.form.pricing_levels,
                shipping_method: this.form.shipping_method,
                picker_id: this.form.picker_id,
                checker_id: this.form.checker_id,
                return_reason: this.form.return_reason,
                purchaser_id: this.form.purchaser_id,
                transaction_type: this.form.transaction_type,
                sort_column: this.form.sort_column,
                sort_direction: this.form.sort_direction,
            });
        },

        setDateRange() {
            if(this.filters.from !== null && this.filters.to !== null) {
                this.date = [];
                this.date.push(this.formatDateToJS(this.filters.from));
                this.date.push(this.formatDateToJS(this.filters.to));
            }
        },

        handleUpdateDateRange(dateRange) {
            this.form.from = dateRange && dateRange.length ? DateTime.fromJSDate(dateRange[0]).toISODate() : null;
            this.form.to = dateRange && dateRange.length ? DateTime.fromJSDate(dateRange[1]).toISODate() : null;
        },

        formatDateToJS(date) {
            return DateTime.fromISO(date).toJSDate();
        },

        formatDateRange(dateRange) {
            const start = dateRange[0];
            const end = dateRange[1];

            if(start && end)
            {
                let thisYear = DateTime.now().toFormat('yyyy');
                let startYear = DateTime.fromJSDate(start).toFormat('yyyy');
                let endYear = DateTime.fromJSDate(end).toFormat('yyyy');
                let startDate = startYear === thisYear ? DateTime.fromJSDate(start).toFormat('LLL. d') : DateTime.fromJSDate(start).toFormat("LLL. d, yyyy");
                let endDate = endYear === thisYear ? DateTime.fromJSDate(end).toFormat('LLL. d') : DateTime.fromJSDate(end).toFormat("LLL. d, yyyy");

                return startDate + ' - ' + endDate;
            }

            return null;
        }

    },

    computed: {
        itemSalesFilterLayout() {
            return this.form.report === 'item_sales';
        },
        showCreditCardBrandFilter() {
            return this.form.report === 'transactions';
        },
        orderTypeOptionsForReport() {
            if (['shorts', 'order_defects'].includes(this.form.report)) {
                return [{ value: null, label: 'All' }, ...this.orderTypeOptions];
            }

            return this.orderTypeOptions;
        },
        showOrderTypeFilter() {
            return ['item_sales', 'shorts', 'transactions','order_defects','forecasted_shorts'].includes(this.form.report);
        },
        showItemTypeFilter() {
            return this.form.report === 'item_sales';
        },
        showFilterByFilter() {
            return ['item_sales', 'sales','order_defects'].includes(this.form.report);
        },
        showCategoryFilter() {
            return this.form.report === 'item_sales';
        },
        showCustomerTypeFilter() {
            return ['item_sales', 'retail'].includes(this.form.report);
        },
        showPricingLevelFilter() {
            return this.form.report === 'item_sales' && this.form.order_type !== 'retail';
        },
        showShippingMethodFilter() {
            return this.form.report === 'item_sales' || this.form.report === 'shorts' || this.form.report === 'forecasted_shorts';
        },
        showPwFilter() {
            return this.form.report === 'item_sales' && this.form.order_type !== 'wholesale';
        },
        reportHasExportableRows() {
            const d = this.report_data;
            if (d == null) {
                return false;
            }
            if (Array.isArray(d)) {
                return d.length > 0;
            }
            if (this.form.report === 'sales' && Array.isArray(d.sales)) {
                return d.sales.length > 0;
            }
            if (this.form.report === 'order_defects' && Array.isArray(d.orders)) {
                return d.orders.length > 0;
            }
            return false;
        },
        canRunReport() {
            const hasDateRange = this.form.from != null && this.form.to != null;

            return this.form.report != null && hasDateRange;
        },
        defaultSortColumn() {
            // Set the default sort column depending on the report selection
            if (this.form.report === 'item_sales') return 'title';
            if (this.form.report === 'transactions') return 'credit_card_brand_name';
            else return 'id';   // Probably safe
        },
        showInitialReportMsg() {
            return this.form.report == null ||
                (this.form.report != null && this.form.isDirty && this.report_data.length == 0 );
        },
        showNoReportDataMsg() {
            return this.form.report != null && !this.form.isDirty && this.report_data.length == 0;
        },
        categoryOptions() {
            let categoryOptions = [];

            let selectedParentCategoryIds = [];
            this.categories.forEach(category => {
                if (category.parent_id === null && this.form.category_ids && this.form.category_ids.includes(category.id))
                {
                    selectedParentCategoryIds.push(category.id);
                }
            });

            this.categories.forEach(category => {
                if (!selectedParentCategoryIds.includes(category.parent_id))
                {
                    categoryOptions.push(category);
                }
            });

            return categoryOptions;
        },
        customerTypeOptions() {
            const ct = this.customer_types || [];
            if (!ct.length) return [];
            const first = ct[0];
            if (typeof first === 'object' && first !== null && 'id' in first) return ct;
            return ct.map(t => ({ id: t, slug: String(t), title: String(t) }));
        }
    },

    watch: {
        'form.report': {
            immediate: true,
            deep: true,
            handler: function() {
                this.form.sort_column = this.defaultSortColumn;
                this.form.default_sort_column = this.defaultSortColumn;
            }
        },

        data: {
            immediate: true,
            handler (newValue) {
                this.report_data = newValue;
            }
        }
    },

    mounted() {
        this.setDateRange();
    }
}
</script>

<style scoped>
.multi-custom {
    min-height: 1.5rem;
}
</style>

<style>
.dp-custom-input {
    @apply h-7;
}

.report-container thead {
    position: sticky;
    top: 0;
}
.report-container tfoot {
    position: sticky;
    bottom: 0;
}
.report-container tfoot td {
    height: 1.5rem;
    line-height: 1.5rem;
    background-color: #efefef;
    @apply border-gray border-b-0 border-t-2;
    @apply font-bold text-sm;
    @apply pt-2 pb-1 px-2;
}
</style>
